// Sales replay data for the Admin: API calls and TanStack Query options, every response
// parsed with the shared @enp/domain/api schema.

import {
  type ReplayConsumption,
  type ReplayRunDetail,
  type ReplayScenario,
  importConnectionListResponseSchema,
  importConnectorListResponseSchema,
  REPLAY_PAGE_MAX,
  REPLAY_RUN_BUSY_STATUSES,
  replayConsumptionResponseSchema,
  replayRunDetailSchema,
  replayScenarioListResponseSchema,
  type replayScenarioCreateRequestSchema,
  replayScenarioSchema,
  replayUnmappedItemsResponseSchema,
  replayUnmappedLinesResponseSchema,
  replayVatMismatchesResponseSchema,
} from '@enp/domain/api';
import { infiniteQueryOptions, type QueryClient, queryOptions } from '@tanstack/react-query';
import type { z } from 'zod';
import { api } from '../lib/api.js';

/** Rows per page of a report's lists. */
export const ROWS_PAGE = 100;

/** How often a busy run is asked for its progress. */
export const POLL_MS = 1500;

export const replayKeys = {
  all: ['replay'] as const,
  scenarios: () => ['replay', 'scenarios'] as const,
  run: (id: string) => ['replay', 'run', id] as const,
  rows: (id: string, list: RowsKind) => ['replay', 'run', id, list] as const,
  connections: () => ['replay', 'connections'] as const,
  connectors: () => ['replay', 'connectors'] as const,
};

export const isBusy = (status: ReplayRunDetail['status']) =>
  (REPLAY_RUN_BUSY_STATUSES as readonly string[]).includes(status);

export const scenariosQuery = () =>
  queryOptions({
    queryKey: replayKeys.scenarios(),
    queryFn: ({ signal }) =>
      api('/replay/scenarios', { signal, schema: replayScenarioListResponseSchema }),
    retry: false,
  });

/** A run; polls while a job works on it. */
export const runQuery = (id: string) =>
  queryOptions({
    queryKey: replayKeys.run(id),
    queryFn: ({ signal }) =>
      api(`/replay/runs/${encodeURIComponent(id)}`, { signal, schema: replayRunDetailSchema }),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && isBusy(status) ? POLL_MS : false;
    },
    retry: false,
  });

/** Sources a replay can read: the import connections (names come from the API). */
export const connectionsQuery = () =>
  queryOptions({
    queryKey: replayKeys.connections(),
    queryFn: ({ signal }) =>
      api('/imports/connections', { signal, schema: importConnectionListResponseSchema }),
    staleTime: 60_000,
    retry: false,
  });

/** Connectors with their capabilities (only `sales_source` ones can be replayed). */
export const connectorsQuery = () =>
  queryOptions({
    queryKey: replayKeys.connectors(),
    queryFn: ({ signal }) =>
      api('/imports/connectors', { signal, schema: importConnectorListResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });

// ---------------------------------------------------------------- report lists

export const REPLAY_LISTS = ['unmapped', 'vat', 'consumption'] as const;
export type ReplayList = (typeof REPLAY_LISTS)[number];

/** A list the report reads: one of the tabs, or the unmapped lines grouped by item. */
type RowsKind = ReplayList | 'items';

const LIST_PATH: Record<RowsKind, string> = {
  items: 'unmapped-items',
  unmapped: 'unmapped-lines',
  vat: 'vat-mismatches',
  consumption: 'consumption',
};

/** A page of one of a run's row lists. */
export interface RowsPage<T> {
  items: T[];
  next_cursor: string | null;
  total: number;
}

function rowsPath(runId: string, list: RowsKind, cursor: string | null, limit = ROWS_PAGE) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return `/replay/runs/${encodeURIComponent(runId)}/${LIST_PATH[list]}?${params.toString()}`;
}

function rowsQuery<Page extends RowsPage<unknown>>(
  runId: string,
  list: RowsKind,
  schema: z.ZodType<Page>,
) {
  return infiniteQueryOptions({
    queryKey: replayKeys.rows(runId, list),
    queryFn: ({ pageParam, signal }) => api(rowsPath(runId, list, pageParam), { signal, schema }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page) => last.next_cursor,
    retry: false,
  });
}

/** Lines no product matched, a page at a time (cursor-paged). */
export const unmappedQuery = (runId: string) =>
  rowsQuery(runId, 'unmapped', replayUnmappedLinesResponseSchema);
/**
 * The unmapped lines grouped by what was sold where (review UI2A-7), with the latest
 * catalogue import of the source (`import_run_id`, for the link to map them).
 */
export const unmappedItemsQuery = (runId: string) =>
  rowsQuery(runId, 'items', replayUnmappedItemsResponseSchema);
/** Lines whose VAT differs from the source's. */
export const vatQuery = (runId: string) =>
  rowsQuery(runId, 'vat', replayVatMismatchesResponseSchema);
/**
 * Every product's usage, page after page (one row per product, so a catalogue's worth).
 * The table sorts it on the screen, so it needs every row first: sorting a partial list
 * would hide the largest rows on pages not loaded yet (review CR2-30).
 */
export const consumptionQuery = (runId: string) =>
  queryOptions({
    queryKey: replayKeys.rows(runId, 'consumption'),
    queryFn: ({ signal }) => fetchAllConsumption(runId, signal),
    retry: false,
  });

/** Every product's usage (the table and the export), page after page. */
export async function fetchAllConsumption(
  runId: string,
  signal?: AbortSignal,
): Promise<ReplayConsumption[]> {
  const rows: ReplayConsumption[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 10_000; page++) {
    const res: RowsPage<ReplayConsumption> = await api(
      rowsPath(runId, 'consumption', cursor, REPLAY_PAGE_MAX),
      { signal, schema: replayConsumptionResponseSchema },
    );
    rows.push(...res.items);
    cursor = res.next_cursor;
    if (!cursor) break;
  }
  return rows;
}

// ---------------------------------------------------------------- commands

/**
 * What `POST /replay/scenarios` takes, before the server's defaults: options left out are
 * the server's to fill in (review CR2-27).
 */
export type ScenarioCreateBody = Omit<
  z.input<typeof replayScenarioCreateRequestSchema>,
  'idempotency_key'
>;

/**
 * Creates a scenario. `idempotencyKey` is one per create attempt, so a retried request
 * (a double click, a dropped answer) never makes two scenarios.
 */
export function createScenario(
  body: ScenarioCreateBody,
  idempotencyKey: string,
): Promise<ReplayScenario> {
  return api('/replay/scenarios', {
    method: 'POST',
    body: { ...body, idempotency_key: idempotencyKey },
    schema: replayScenarioSchema,
  });
}

export function startRun(scenarioId: string): Promise<ReplayRunDetail> {
  return api(`/replay/scenarios/${encodeURIComponent(scenarioId)}/runs`, {
    method: 'POST',
    schema: replayRunDetailSchema,
  });
}

/**
 * Cancels a busy run: answers the run, now `cancelled`. Refused with 409 `run_busy` while
 * a job writes the run's practice branch, and 409 `run_not_active` once it has finished.
 */
export function cancelRun(runId: string): Promise<ReplayRunDetail> {
  return api(`/replay/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    schema: replayRunDetailSchema,
  });
}

/**
 * The run a scenario has going now, asked fresh (after a 409 `run_in_progress`: the start
 * was refused because one is already running).
 */
export async function runningRunOf(
  queryClient: QueryClient,
  scenarioId: string,
): Promise<string | null> {
  const { items } = await queryClient.fetchQuery({ ...scenariosQuery(), staleTime: 0 });
  const last = items.find((s) => s.id === scenarioId)?.last_run;
  return last && isBusy(last.status) ? last.id : null;
}
